import { useCallback, useRef } from 'react';

import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import { Positions } from '../../../ui/utils/xstate/get-ai-experience-service';
import { createAIResponseDecoration, getHiddenRangeDecorations } from '../actions';
import { createCommand } from '../in-editor-streaming-plugin-factory';

export const useInEditorStreaming = ({
	view,
	positions,
}: {
	view: EditorView;
	positions: Positions;
}) => {
	const innerEditorViewRef = useRef<EditorView>();
	const [startPos, endPos] = positions;

	const mountDecorations = useCallback(() => {
		const decorations = getHiddenRangeDecorations({
			state: view.state,
			initialPositions: [startPos, endPos],
		});
		const { widget, innerEditorView } = createAIResponseDecoration(startPos, view.state);

		innerEditorViewRef.current = innerEditorView;

		const decorationSet = DecorationSet.create(view.state.doc, [...decorations, widget]);
		const command = createCommand({
			type: 'START',
			data: {
				decorationSet,
			},
		});

		command(view.state, view.dispatch);
	}, [view, startPos, endPos]);

	const updateStream = useCallback(
		(frag: Fragment) => {
			if (!innerEditorViewRef.current) {
				return;
			}

			/**
			 * We need to re-create the fragment to make it compatible with the inner editor view's schema
			 * Otherwise it will not update the document and fail silently
			 */
			const recreatedFrag = Fragment.fromJSON(
				innerEditorViewRef.current.state.schema,
				frag.toJSON(),
			);
			// check view selection from/to parent, and only break up first / last nodes if it's the same
			if (innerEditorViewRef.current) {
				innerEditorViewRef.current.dispatch(
					innerEditorViewRef.current.state.tr.replaceWith(
						0,
						innerEditorViewRef.current.state.doc.content.size,
						recreatedFrag,
					),
				);
			}
		},
		[innerEditorViewRef],
	);

	return {
		mountDecorations,
		updateStream,
	};
};
